import React, { useMemo, useRef, useState } from "react";
import ModalShell from "./ModalShell";
import NumpadPopover from "../NumpadPopover";
import RosterCombobox from "../RosterCombobox";
import { loadOwner, loadRoster, OWNER_ID } from "../roster";
import { Player, Tee } from "../types";

interface KnownPlayer {
  id: string;
  name: string;
  handicapIndex?: number;
}

interface Props {
  players: Player[];
  max: number;
  tees: Tee[];
  defaultTeeId?: string;
  onChange: (next: Player[]) => void;
  onClose: () => void;
}

const newID = () => `p_${Math.random().toString(36).slice(2, 10)}`;

export default function PlayersModal({
  players,
  max,
  tees,
  defaultTeeId,
  onChange,
  onClose,
}: Props) {
  // Snapshot the roster + owner on open. Roster edits during a round are
  // out of scope (and the round shouldn't write back anyway), so a single
  // snapshot is enough. Owner is included as the first known-player so
  // it shows up in the picker like any other.
  const known: KnownPlayer[] = useMemo(() => {
    const owner = loadOwner();
    const ownerEntry: KnownPlayer[] = owner
      ? [
          {
            id: OWNER_ID,
            name: owner.name,
            handicapIndex: owner.handicapIndex,
          },
        ]
      : [];
    const rosterEntries: KnownPlayer[] = loadRoster().map((r) => ({
      id: r.id,
      name: r.name,
      handicapIndex: r.handicapIndex,
    }));
    return [...ownerEntry, ...rosterEntries];
  }, []);

  // Track the last-added player so its name input gets focus on first
  // render. Click "+" → blank row appears with cursor in name field;
  // the combobox's autoFocus prop handles it on mount.
  const [focusId, setFocusId] = useState<string | null>(null);

  // Per-row roster options — hide names already in the round so the
  // same player can't be picked twice from the dropdown. The row's
  // OWN name stays visible (filter excludes only OTHER rows) so a user
  // who clears their input can still re-pick themselves.
  const optionsFor = (rowId: string) => {
    const taken = new Set(
      players
        .filter((p) => p.id !== rowId)
        .map((p) => p.name.trim().toLowerCase())
        .filter(Boolean),
    );
    return known.filter((k) => !taken.has(k.name.toLowerCase()));
  };

  const updatePlayer = (id: string, patch: Partial<Player>) => {
    onChange(players.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  };

  // Name edits — three cases:
  //   1. Empty / partial → take the value as-is, no auto-fill.
  //   2. Exact match against a known player (owner + roster) AND that
  //      person isn't already in another row → copy name + handicap so
  //      typing a roster name fills the HCP automatically.
  //   3. Match against someone already in the round → keep typed name
  //      as free text (no overwrite, no duplicate add).
  const onNameChange = (id: string, raw: string) => {
    const target = raw.trim().toLowerCase();
    const match = target
      ? known.find((k) => k.name.toLowerCase() === target)
      : undefined;
    const alreadyInRound = match
      ? players.some(
          (p) =>
            p.id !== id &&
            p.name.trim().toLowerCase() === match.name.toLowerCase(),
        )
      : false;
    if (match && !alreadyInRound) {
      updatePlayer(id, { name: match.name, handicap: match.handicapIndex });
    } else {
      updatePlayer(id, { name: raw });
    }
  };

  const remove = (id: string) => {
    onChange(players.filter((p) => p.id !== id));
    if (focusId === id) setFocusId(null);
  };

  // "+" — add a blank row. The datalist on each row's name input lets
  // the user pick a known roster name (drop-arrow shows the full list)
  // OR type a fresh name; both paths flow through onNameChange above.
  const add = () => {
    if (players.length >= max) return;
    const id = newID();
    onChange([...players, { id, name: "", tee: defaultTeeId }]);
    setFocusId(id);
  };

  // ── Drag-to-reorder ──────────────────────────────────────────────────────
  // Pointer-event-based drag so the same code path works on mouse and
  // touch (Capacitor WebView on iOS / Android). HTML5 drag-and-drop is
  // unreliable on touch. The drag handle button captures the pointer on
  // pointerdown; subsequent move events fire on the handle regardless of
  // where the finger / cursor goes. On each move we identify which OTHER
  // row's bounding box the pointer is over and swap the dragged player
  // into that slot. The reorder commits live (via onChange) so the
  // scorecard reflects the new order immediately — same data flow as the
  // existing name/HCP/tee edits.
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

  const onHandleDown = (e: React.PointerEvent<HTMLButtonElement>, id: string) => {
    e.preventDefault();
    setDraggingId(id);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // Older WebViews may reject capture on a button — drag still
      // works because pointermove fires globally during a primary
      // button hold.
    }
  };

  const onHandleMove = (e: React.PointerEvent<HTMLButtonElement>, id: string) => {
    if (draggingId !== id) return;
    const y = e.clientY;
    for (const [otherId, el] of rowRefs.current.entries()) {
      if (!el || otherId === id) continue;
      const rect = el.getBoundingClientRect();
      if (y >= rect.top && y <= rect.bottom) {
        const fromIdx = players.findIndex((p) => p.id === id);
        const toIdx = players.findIndex((p) => p.id === otherId);
        if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;
        const next = [...players];
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        onChange(next);
        return;
      }
    }
  };

  const onHandleUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    setDraggingId(null);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // ignore — capture may already have been lost
    }
  };

  return (
    <ModalShell
      title={`Players (${players.length}/${max})`}
      onClose={onClose}
      footer={
        <button className="cg-btn cg-btn-primary" onClick={onClose}>
          Done
        </button>
      }
    >
      <div className="cg-player-table">
        <div className="cg-player-head">
          <span aria-hidden />
          <span>Player</span>
          <span>HCP</span>
          <span>Tee</span>
          <span aria-hidden />
        </div>

        {players.map((p) => (
          <div
            key={p.id}
            ref={(el) => {
              rowRefs.current.set(p.id, el);
            }}
            className={`cg-player-row${
              draggingId === p.id ? " cg-player-row-dragging" : ""
            }`}
          >
            <button
              type="button"
              className="cg-player-drag-handle"
              aria-label={`Reorder ${p.name || "player"}`}
              title="Drag to reorder"
              onPointerDown={(e) => onHandleDown(e, p.id)}
              onPointerMove={(e) => onHandleMove(e, p.id)}
              onPointerUp={onHandleUp}
              onPointerCancel={onHandleUp}
            >
              ⋮
            </button>
            <RosterCombobox
              value={p.name}
              onChange={(next) => onNameChange(p.id, next)}
              options={optionsFor(p.id)}
              placeholder="Name"
              autoFocus={p.id === focusId}
              className="cg-player-name-combo"
            />
            <NumpadPopover
              value={p.handicap}
              onChange={(next) => updatePlayer(p.id, { handicap: next })}
              min={0}
              max={54}
              maxDecimals={1}
              displayDecimals={1}
              ariaLabel={`Handicap index for ${p.name || "player"}`}
              className="cg-player-hcp-num"
            />
            <select
              className="cg-player-tee"
              value={p.tee || ""}
              onChange={(e) => updatePlayer(p.id, { tee: e.target.value })}
            >
              {tees.length === 0 && <option value="">—</option>}
              {tees.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <button
              className="cg-player-remove"
              onClick={() => remove(p.id)}
              aria-label={`Remove ${p.name || "player"}`}
              title="Remove"
            >
              ×
            </button>
          </div>
        ))}

        {players.length < max && (
          <button
            className="cg-player-add"
            onClick={add}
            aria-label="Add player"
            title="Add player"
          >
            +
          </button>
        )}
      </div>
    </ModalShell>
  );
}
